import { Link } from "react-router-dom";
import { Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ShortlistPanelProps {
  favorites: Array<{
    id: string;
    slug: string;
    title: string;
    image: string | null;
    price: number | null;
    rating: number | null;
    subtitle: string | null;
  }> | undefined;
}

export function ShortlistPanel({ favorites }: ShortlistPanelProps) {
  if (!favorites || favorites.length === 0) {
    return (
      <div className="border rounded-lg p-6 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Your Shortlist</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Tools you're seriously considering
        </p>
        <div className="text-center py-8">
          <Heart className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground mb-4">
            No tools in your shortlist yet. Browse the marketplace to find tools that fit your needs.
          </p>
          <Link to="/browse">
            <Button variant="outline">
              Browse Tools
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 bg-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Your Shortlist</h2>
        </div>
        <Link to="/account/favorites">
          <Button variant="ghost" size="sm">
            View All
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Tools you're seriously considering
      </p>

      <div className="space-y-3">
        {favorites.map((product) => (
          <Link
            key={product.id}
            to={`/product/${product.slug}`}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
          >
            <img
              src={product.image || '/placeholder.svg'}
              alt={product.title}
              className="h-12 w-12 rounded object-cover"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-sm truncate">{product.title}</h3>
              <p className="text-xs text-muted-foreground truncate">
                {product.subtitle || 'No description'}
              </p>
            </div>
            <div className="text-right">
              {product.price !== null && (
                <p className="text-sm font-semibold">
                  ${product.price}
                  <span className="text-xs text-muted-foreground">/mo</span>
                </p>
              )}
              {product.rating && (
                <p className="text-xs text-muted-foreground">
                  ⭐ {product.rating.toFixed(1)}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {favorites.length >= 6 && (
        <Link to="/account/favorites" className="block mt-4">
          <Button variant="outline" className="w-full">
            View All Favorites
          </Button>
        </Link>
      )}
    </div>
  );
}
